import dayjs from "dayjs";
import { BaseFilter, GroupOptionType } from "src/constants";
import { Hotel } from "src/store/slices/hotels/Hotels";
import { compactNumber } from "src/utility";
import i18n from "src/i18n";

const getFacilityCategories = (categorizedFacilities: { category: string; items: string[] }[]) =>
  categorizedFacilities?.map((facility) => facility.category) ?? [];

const STATIC_FILTERS = [
  {
    get title() {
      return i18n.t("Hotels.listing.checkinTime");
    },
    op: "between",
    key: "checkIn",
    component: "TimeSegments",
    category: "number",
    path: ["checkInTime"],
    transform: (value) => dayjs(value, "HH").hour(),
  },
  {
    get title() {
      return i18n.t("Hotels.listing.checkoutTime");
    },
    op: "between",
    key: "checkOut",
    category: "number",
    component: "TimeSegments",
    path: ["checkOutTime"],
    transform: (value) => dayjs(value, "HH").hour(),
  },
  {
    get title() {
      return i18n.t("Hotels.listing.hotelRating");
    },
    op: ">=",
    key: "hotelRate",
    category: "number",
    component: "Rating",
    path: ["hotelRating"],
  },
  {
    key: "facilities",
    category: "string|number[]",
    path: ["categorizedFacilities"],
    op: "==",
    component: "FacilitiesSelect",
    get title() {
      return i18n.t("Hotels.listing.facilities");
    },
    transform: getFacilityCategories,
  },
] as const satisfies BaseFilter[];

const PRICE_FILTER = [
  {
    key: "price",
    category: "number",
    path: ["minPrice"],
    op: "between",
    component: "range",
    get title() {
      return i18n.t("Hotels.listing.price");
    },
    dependencies:[{
      key:"currency",path:["currency"]
    }],
    labelTransform: (val, deps) => `${compactNumber(val)} ${deps?.currency}`,
  },
] as const satisfies BaseFilter[];

const DYNAMIC_FILTERS = [...PRICE_FILTER];

const GROUP_OPTIONS = [
  { key: "price", category: "number", path: ["minPrice"] },
  {
    key: "hotelRate",
    category: "number",
    path: ["hotelRating"],
  },
  {
    key: "facilities",
    category: "string|number[]",
    path: ["categorizedFacilities"],
    transform: getFacilityCategories,
  },
] as const satisfies GroupOptionType[];

const ALL_FILTERS = [...DYNAMIC_FILTERS, ...STATIC_FILTERS];

export type HotelFilterKeys = (typeof STATIC_FILTERS)[number]["key"] | (typeof DYNAMIC_FILTERS)[number]["key"];

export type DataGroupType = Map<(typeof GROUP_OPTIONS)[number]["key"], Map<any, Hotel[]>>;

const SORTERS: BaseFilter[] = [
  {
    key: "price",
    category: "number",
    path: ["minPrice"],
    get title() {
      return i18n.t("Hotels.listing.cheapest");
    },
    component: "sorterButton",
    op: "==",
  },
  {
    key: "hotelRate",
    op: "==",
    category: "number",
    path: ["hotelRating"],
    get title() {
      return i18n.t("Hotels.listing.hotelRating");
    },
    component: "sorterButton",
  },
];

export const ALL_SORTERS = [...SORTERS];

const INITIAL_SORTER_VALUE = {
  price: false,
};

export { GROUP_OPTIONS, SORTERS, ALL_FILTERS, STATIC_FILTERS, DYNAMIC_FILTERS, INITIAL_SORTER_VALUE, getFacilityCategories };
